/* A headline figure tile. Rank, top to bottom (33-ui-build-spec): the figure is the largest thing
   and full ink; its label above and unit beside it are muted; the delta, note and drill link are
   small print. Hierarchy is weight and size, and **no figure is ever coloured** by the brand.

   Two tones. The tone only re-points the `--kt-*` variables, so every rule below is written once
   and neither tone can drift from the other. The variables name tokens; no colour literal exists
   here (ADR-0007).

   **Nothing in this file may be named `figure...` except the classes below.** `ui-check` finds
   every clickable KPI by `[class*="_figure_"]` and expects each to open a breakdown. */

.light {
  --kt-bg: var(--panel);
  --kt-line: var(--line);
  --kt-ink: var(--ink);
  --kt-ink-2: var(--ink-2);
  --kt-muted: var(--muted);
  --kt-link: var(--accent);
  --kt-link-hover: var(--accent-h);
  --kt-rule: var(--line-2);
}

.dark {
  --kt-bg: var(--hero-card);
  --kt-line: var(--hero-line);
  --kt-ink: var(--hero-ink);
  --kt-ink-2: var(--hero-ink-2);
  --kt-muted: var(--hero-muted);
  --kt-link: var(--hero-link);
  --kt-link-hover: var(--hero-ink);
  --kt-rule: var(--hero-muted);
}

.tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px 16px;
  background: var(--kt-bg);
  border: 1px solid var(--kt-line);
  border-radius: var(--radius-card);
  color: var(--kt-ink);
}

/* Sentence case, muted: the label of `ui/Tile` (12 px, 600). This tile used to set its label in tracked
   capitals, which made the filing's own views read as another product beside the table pages (design
   review 2026-11-03, systems 3 and persona 9). */
.label {
  color: var(--kt-muted);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The figure is a button set as text. The dotted underline says it opens its receipt; it turns
   solid on intent. */
.figure {
  display: block;
  max-width: 100%;
  margin: 4px 0 0;
  padding: 0 0 7px;

  /* As tall as a headline figure, so a tile whose value is words (smaller) starts its note on the
     same line as the tile beside it that holds a number (alignment audit, 2026-11-03). */
  min-height: calc(var(--t-kpi) * 1.15 + 7px);
  border: 0;
  border-radius: 3px;
  background: none;
  font: inherit;
  font-size: var(--t-kpi);
  font-weight: 700;
  font-variant-numeric: lining-nums tabular-nums;
  letter-spacing: -0.03em;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  cursor: pointer;
  color: var(--kt-ink);
  text-decoration: underline dotted var(--kt-rule);
  text-decoration-thickness: 1px;
  text-underline-offset: 6px;
}

.figure:hover {
  text-decoration-style: solid;
  text-decoration-color: var(--kt-link);
}

.figure:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
}

.dark .figure:focus-visible {
  outline-color: var(--hero-link);
}

/* A figure with no receipt of its own is not a link and must not look like one. */
.figureStatic {
  text-decoration: none;
  cursor: default;
}

/* A value that is words. Same weight and ink, a step down so "Not checked" fits a tile. */
.textual {
  font-size: var(--t-figure);

  /* **Words wrap; only a number may be cut** (responsive pass, 2026-10-30). The factor detail's
     Edition tile read "2026 v1.2, flat file revised 31 …" at 390px — the one fact the tile exists
     to state, ellipsised, with its full text nowhere on the screen. A figure is one unbreakable
     token and keeps its ellipsis; a value that is a sentence takes the lines it needs. */
  white-space: normal;
  overflow-wrap: anywhere;
  text-overflow: clip;
}

/* The headline total. Larger, and still full ink in the tile's own tone. */
.large {
  font-size: 56px;
  line-height: 1;
  letter-spacing: -0.04em;
}

.unit {
  margin-left: 6px;
  color: var(--kt-muted);
  font-size: var(--t-dense);
  font-weight: 500;
  letter-spacing: 0;
}

.large .unit {
  font-size: var(--t-head);
}

.note {
  margin-top: 4px;
  color: var(--kt-muted);
  font-size: var(--t-min);
  line-height: 1.4;
  /* **Not cut** (gate 37, WCAG 1.4.12). This was `max-height: 2.8em; overflow: hidden`, two lines and
     then silence, with the sentence only in `title`, which a keyboard or touch reader never gets. With
     the text-spacing override the same sentence needs a third line and the tile lost half of it. The
     note takes the lines it needs; the tile grows by a line, which the shell's own grid absorbs. */
  overflow-wrap: anywhere;
}

.noteStrong {
  color: var(--kt-ink-2);
  font-weight: 600;
}

.foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  min-height: 32px;
}

.drill {
  min-width: 0;
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 1.3;
}

/* A link is 24px tall at least (WCAG 2.5.8) and has hover and active states of its own. The colour
   comes from the tone: the screen builds the link with its own class, and this is more specific. */
.drill a {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  color: var(--kt-link);
  font-size: var(--t-dense);
  font-weight: 600;
}

.drill a:hover {
  color: var(--kt-link-hover);
  text-decoration-thickness: 2px;
}

/* The thumbnail sits on a light chip in either tone, because the chart draws in colours measured
   against a light surface. */
.trend {
  flex: 0 0 auto;
  padding: 3px 6px;
  border-radius: var(--radius-ctl);
  background: var(--panel);
}
